import * as React from 'react';
import classnames from 'classnames';
import { Alert, Breadcrumbs } from '@grandvisionhq/common';
import { Text } from '@grandvisionhq/elements';
import { Types } from '@grandvisionhq/graphql';
import { Location, Types as Types$1, OpeningHours } from '@grandvisionhq/locations';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { areServicesAvailable } from '../../utils/store-utils.js';
import useIsInViewport from '../../hooks/useIsInViewport.js';
import { getActiveStoreOffers } from '../../utils/get-active-store-offers.js';
import { getActiveCampaignBanner } from '../../utils/get-active-campaign-banner.js';
import usePreferredStore from '../../hooks/use-preferred-store.js';
import StoreDistance from '../../components/StoreDistance/StoreDistanceContainer.js';
import SimpleBreadcrumb from '../../components/SimpleBreadcrumb/SimpleBreadcrumb.js';
import StoreDetailImage from '../../components/StoreDetailImage/StoreDetailImageContainer.js';
import StoreBookAppointmentCTA from '../../components/StoreBookAppointmentCTA/StoreBookAppointmentCTAContainer.js';
import StoreLocation from '../../components/StoreLocation/StoreLocationContainer.js';
import StoreFacilities from '../../components/StoreFacilities/StoreFacilitiesContainer.js';
import StoreRating from '../../components/StoreRating/StoreRatingContainer.js';
import StoreOffers from '../../components/StoreOffers/StoreOffersContainer.js';
import NearbyStores from '../../components/NearbyStores/NearbyStoresContainer.js';
import StoreCategories from '../../components/StoreCategories/StoreCategoriesContainer.js';
import StoreDescription from '../../components/StoreDescription/StoreDescriptionContainer.js';
import StoreFranchiseIndicator from '../../components/StoreFranchiseIndicator/StoreFranchiseIndicatorContainer.js';
import StoreBrands from '../../components/StoreBrands/StoreBrandsContainer.js';
import StorePaymentMethods from '../../components/StorePaymentMethods/StorePaymentMethodsContainer.js';
import StoreSocialMedia from '../../components/StoreSocialMedia/StoreSocialMediaContainer.js';

const StoreDetailsRoot = ({ store , opco , hidePhone , hideEmail , breadcrumbs , urls , appointmentTypeId , logos , dropInAppointmentBookingWidget: DropInWidget = null , nearestStores =[] , campaignBanner , productCategories , preferredStoreButton ,  })=>{
    var ref;
    const { getFormattedLabel  } = useIntl();
    const canShowBookAppointmentWidget = areServicesAvailable(store, [
        Types.ServicesNotAvailable.APPOINTMENTS
    ]) && Boolean((ref = urls.bookAppointment) === null || ref === void 0 ? void 0 : ref.length);
    const showBookAppointmentDropInWidget = canShowBookAppointmentWidget && Boolean(appointmentTypeId === null || appointmentTypeId === void 0 ? void 0 : appointmentTypeId.length) && DropInWidget;
    const showBookAppointmentCTA = canShowBookAppointmentWidget && !showBookAppointmentDropInWidget;
    const { name , shortName , description , detailImage , emailAddress , phone , streetName , streetNumber , postalCode , town , province , country , code , additionalStreetInfo , lat , lon , storeNote , openingHours , specialOpeningHours , facilities , brands , paymentMethods , socialMediaLinks , offers , wheelchairAccessible , storeProductCategories , hearingAppointmentPossible , isFranchiseStore ,  } = store;
    const bookAppointmentButtonRef = React.useRef(null);
    const dropInWidgetRef = React.useRef(null);
    const isBookAppointmentButtonInViewport = useIsInViewport(bookAppointmentButtonRef);
    const isDropInWidgetInViewport = useIsInViewport(dropInWidgetRef);
    const storeCategories = getActiveStoreOffers(productCategories, storeProductCategories);
    const activeCampaignBanner = getActiveCampaignBanner(campaignBanner, {
        storeCode: code
    });
    const { preferredStoreUpdated , preferredStoreSuccess , preferredStoreAlertLabel  } = usePreferredStore(shortName !== null && shortName !== void 0 ? shortName : name);
    const { featureConfigs: { storeDistanceConfig  } = {}  } = useFeatureToggles();
    const hideStickyBookAppointmentButton = isBookAppointmentButtonInViewport || isDropInWidgetInViewport;
    const showPaymentMethods = !!paymentMethods && !!(logos === null || logos === void 0 ? void 0 : logos.paymentMethods) && paymentMethods.length > 0 && logos.paymentMethods.length > 0;
    const showSocialMedia = !!socialMediaLinks && !!(logos === null || logos === void 0 ? void 0 : logos.socialMedia) && socialMediaLinks.length > 0 && logos.socialMedia.length > 0;
    const addressData = React.useMemo(()=>({
            additionalStreetInfo,
            streetName,
            streetNumber,
            postalCode,
            town,
            province
        })
    , [
        additionalStreetInfo,
        streetName,
        streetNumber,
        postalCode,
        town,
        province
    ]);
    const distanceDetails = React.useMemo(()=>storeDistanceConfig ? /*#__PURE__*/ React.createElement(StoreDistance, {
            code: code
        }) : null
    , [
        code,
        storeDistanceConfig
    ]);
    const labels = React.useMemo(()=>{
        const label = getFormattedLabel('stores.storeDetailsRoot.announcement', {
            hideLabelKey: true,
            parseLink: true
        });
        if (!label) {
            return {};
        }
        return {
            storeAnnouncement: label
        };
    }, [
        getFormattedLabel
    ]);
    var _bookAppointment, _bookAppointment1;
    return /*#__PURE__*/ React.createElement("article", {
        className: "store-details-stores-v2"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__top-wrapper"
    }, preferredStoreUpdated ? /*#__PURE__*/ React.createElement(Alert, {
        type: preferredStoreSuccess ? 'success' : 'warning',
        icon: preferredStoreSuccess ? 'checkmark-circle' : 'warning',
        message: preferredStoreAlertLabel
    }) : null, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__top-row"
    }, breadcrumbs && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__breadcrumbs"
    }, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: breadcrumbs
    })), breadcrumbs.length >= 2 && /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__breadcrumb"
    }, /*#__PURE__*/ React.createElement(SimpleBreadcrumb, {
        title: breadcrumbs[1].name,
        href: breadcrumbs[1].url
    }))), preferredStoreButton ? preferredStoreButton : null)), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__content-wrapper"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__main-content-wrapper"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__main-content"
    }, /*#__PURE__*/ React.createElement(StoreDetailImage, {
        className: "store-details-stores-v2__detail-image",
        detailImageUrl: detailImage,
        fallbackImage: urls.storeFallbackImage,
        alt: shortName || name
    }), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__contact-info"
    }, /*#__PURE__*/ React.createElement(Location, {
        className: "store-details-stores-v2__location",
        id: code,
        name: name,
        variant: Types$1.LocationVariant.StandAlone,
        emailAddress: emailAddress,
        phone: phone,
        emailHidden: hideEmail,
        phoneHidden: hidePhone,
        locationDetailsUrl: '',
        headerImage: activeCampaignBanner,
        highlight: false,
        addressData: addressData,
        addressSize: "COMPLETE",
        wheelchairAccessible: wheelchairAccessible,
        hearingAppointmentPossible: hearingAppointmentPossible,
        isFranchiseStore: isFranchiseStore,
        distanceDetails: distanceDetails,
        openingHours: /*#__PURE__*/ React.createElement(OpeningHours, {
            withTitle: true,
            announcement: storeNote,
            openingHours: openingHours,
            specialOpeningHours: specialOpeningHours,
            isExpandable: false,
            isExpanded: true
        }),
        buttons: /*#__PURE__*/ React.createElement(React.Fragment, null, showBookAppointmentDropInWidget ? /*#__PURE__*/ React.createElement("div", {
            className: "store-details-stores-v2__drop-in-widget-container",
            ref: dropInWidgetRef
        }, /*#__PURE__*/ React.createElement(DropInWidget, {
            urls: urls,
            storeId: code,
            typeId: appointmentTypeId,
            dataLayerEvents: {
                storeAppointmentClickEvent: 'storeDetailsFastAppointmentClick',
                storeChangeAppointmentTypeClickEvent: 'storeDetailsChangeClick'
            }
        })) : null, showBookAppointmentCTA ? /*#__PURE__*/ React.createElement("div", {
            ref: bookAppointmentButtonRef,
            className: "store-details-stores-v2__book-appointment"
        }, /*#__PURE__*/ React.createElement(StoreBookAppointmentCTA, {
            url: (_bookAppointment = urls.bookAppointment) !== null && _bookAppointment !== void 0 ? _bookAppointment : '',
            code: code,
            name: name,
            town: town,
            country: country,
            streetName: streetName,
            streetNumber: streetNumber,
            additionalStreetInfo: additionalStreetInfo,
            postalCode: postalCode,
            appointmentTypeId: appointmentTypeId,
            storeCode: store.code
        })) : null, (showBookAppointmentDropInWidget || showBookAppointmentCTA) && labels.storeAnnouncement && /*#__PURE__*/ React.createElement(Text, {
            variant: "caption",
            children: labels.storeAnnouncement
        }))
    }))), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__secondary-content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__location"
    }, /*#__PURE__*/ React.createElement(StoreLocation, {
        opco: opco,
        code: code,
        name: name,
        town: town,
        province: province,
        country: country,
        streetName: streetName,
        streetNumber: streetNumber,
        additionalStreetInfo: additionalStreetInfo,
        postalCode: postalCode,
        lat: lat,
        lon: lon,
        distanceDetails: distanceDetails,
        practicalInformation: store.practicalInformation
    })), facilities && facilities.length > 0 && /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__facilities"
    }, /*#__PURE__*/ React.createElement(StoreFacilities, {
        facilities: facilities
    })), /*#__PURE__*/ React.createElement(StoreRating, {
        store: store
    }), offers && offers.length > 0 && /*#__PURE__*/ React.createElement(StoreOffers, {
        className: "store-details-stores-v2__store-offers",
        fallbackImage: urls.offerFallbackImage,
        offers: offers
    }), nearestStores.length > 0 && /*#__PURE__*/ React.createElement(NearbyStores, {
        className: "store-details-stores-v2__nearest-stores",
        stores: nearestStores,
        urls: urls,
        storeDetails: store
    }), storeCategories && storeCategories.length > 0 && /*#__PURE__*/ React.createElement(StoreCategories, {
        className: "store-details-stores-v2__store-product-groups",
        fallbackImage: urls.productCategoryFallbackImage,
        storeCategories: storeCategories
    }), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__description"
    }, /*#__PURE__*/ React.createElement(StoreDescription, {
        storeName: name,
        description: description
    }), isFranchiseStore && /*#__PURE__*/ React.createElement(StoreFranchiseIndicator, null)), brands && brands.length > 0 && /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__brands"
    }, /*#__PURE__*/ React.createElement(StoreBrands, {
        brands: brands
    })), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__payment-methods-social-media"
    }, showPaymentMethods && !!logos && /*#__PURE__*/ React.createElement(StorePaymentMethods, {
        className: "store-details-stores-v2__payment-methods",
        logos: logos.paymentMethods,
        paymentMethods: paymentMethods
    }), showSocialMedia && !!logos && /*#__PURE__*/ React.createElement(StoreSocialMedia, {
        className: "store-details-stores-v2__social-media",
        logos: logos.socialMedia,
        socialMediaLinks: socialMediaLinks
    })))), /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__side-content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-details-stores-v2__detail-image"
    }, /*#__PURE__*/ React.createElement(StoreDetailImage, {
        detailImageUrl: detailImage,
        fallbackImage: urls.storeFallbackImage,
        alt: shortName || name
    })), /*#__PURE__*/ React.createElement(StoreRating, {
        store: store
    }), offers && offers.length > 0 && /*#__PURE__*/ React.createElement(StoreOffers, {
        className: "store-details-stores-v2__store-offers",
        fallbackImage: urls.offerFallbackImage,
        offers: offers
    }), nearestStores.length > 0 && /*#__PURE__*/ React.createElement(NearbyStores, {
        className: "store-details-stores-v2__nearest-stores",
        stores: nearestStores,
        urls: urls,
        storeDetails: store
    }), storeCategories && storeCategories.length > 0 && /*#__PURE__*/ React.createElement(StoreCategories, {
        className: "store-details-stores-v2__store-product-groups",
        fallbackImage: urls.productCategoryFallbackImage,
        storeCategories: storeCategories
    }), showPaymentMethods && logos && /*#__PURE__*/ React.createElement(StorePaymentMethods, {
        logos: logos.paymentMethods,
        paymentMethods: paymentMethods
    }), showSocialMedia && logos && /*#__PURE__*/ React.createElement(StoreSocialMedia, {
        logos: logos.socialMedia,
        socialMediaLinks: socialMediaLinks
    }))), showBookAppointmentCTA && /*#__PURE__*/ React.createElement("div", {
        "data-t": "cta-sticky-footer",
        className: classnames('store-details-stores-v2__sticky-footer', {
            'store-details-stores-v2__sticky-footer--hidden': hideStickyBookAppointmentButton
        })
    }, /*#__PURE__*/ React.createElement(StoreBookAppointmentCTA, {
        url: (_bookAppointment1 = urls.bookAppointment) !== null && _bookAppointment1 !== void 0 ? _bookAppointment1 : '',
        code: code,
        name: name,
        town: town,
        country: country,
        streetName: streetName,
        streetNumber: streetNumber,
        additionalStreetInfo: additionalStreetInfo,
        postalCode: postalCode,
        appointmentTypeId: appointmentTypeId,
        storeCode: store.code
    })));
};
var StoreDetailsRoot$1 = StoreDetailsRoot;

export { StoreDetailsRoot$1 as default };
//# sourceMappingURL=StoreDetailsRoot.js.map
